import React from "react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import Meta from "../Meta";
import Loader from "../loader";
import { t } from "@lingui/core/macro";
import { Trans } from "@lingui/react/macro";
import { applyLang } from "../../../i18n";
import renderIcon from "../../../components/renderIcon";
import { ASSET_URL } from "../../../config";
import Link from "next/link";
import { getBethHabadLabel, walkMinutesFrom } from "../utils";
import StarRating from "../components/StarRating";

/**
 * Ajoute l'affiliateId à une URL Booking.com si fourni (paramètre `aid=`).
 * Non destructif : si l'URL est invalide ou l'id absent, renvoie l'URL originale.
 */
function withAffiliate(url, affiliateId) {
    if (!url || !affiliateId) return url;
    try {
        const u = new URL(url);
        u.searchParams.set("aid", affiliateId);
        return u.toString();
    } catch {
        return url;
    }
}

/**
 * Construit une URL Booking.com pour un hôtel. Utilise `bookingUrl` si fournie
 * par l'API, sinon fallback via la recherche par `bookingId` (stable).
 */
function bookingHotelUrl(place) {
    if (place.bookingUrl) return place.bookingUrl;
    if (place.bookingId) {
        // Endpoint de redirection Booking : redirige directement vers la fiche
        // de l'hôtel sans passer par les résultats de recherche.
        return `https://www.booking.com/searchresults.html?dest_id=${place.bookingId}&dest_type=hotel`;
    }
    return null;
}

/**
 * URL Airbnb pour un logement. Utilise `airbnbUrl` si fournie par l'API,
 * sinon fallback sur `/rooms/{airbnbId}` (format canonique stable).
 */
function airbnbListingUrl(place) {
    if (place.airbnbUrl) return place.airbnbUrl;
    if (place.airbnbId) {
        return `https://www.airbnb.com/rooms/${place.airbnbId}`;
    }
    return null;
}

const hotelStyles = `
.tr-hotels__title {
  font-size: clamp(24px, 4vw, 40px);
  font-weight: 800;
  color: #1a1a2e;
  text-align: center;
  margin: 56px 0 32px;
  padding: 0 16px;
}

/* ── Filtres catégories ── */
.tr-hotels__cats {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 12px;
  margin-bottom: 48px;
  padding: 0 16px;
}
.tr-hotels__cat {
  padding: 10px 22px;
  border-radius: 999px;
  border: none;
  background: color-mix(in srgb, var(--primary, #1b579e) 12%, white);
  color: var(--primary, #1b579e);
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: background .15s, color .15s, transform .15s;
}
.tr-hotels__cat:hover {
  background: color-mix(in srgb, var(--primary, #1b579e) 20%, white);
}
.tr-hotels__cat--active {
  background: var(--primary, #1b579e);
  color: white;
}

/* ── Section par catégorie ── */
.tr-hotels__section {
  margin-bottom: 56px;
}
.tr-hotels__section-title {
  font-size: clamp(20px, 2.5vw, 28px);
  font-weight: 700;
  color: #1a1a2e;
  text-align: center;
  margin: 0 0 28px;
}

/* ── Grille ── */
.tr-hotels__grid {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 20px;
  max-width: 1180px;
  margin: 0 auto;
  padding: 0 16px;
}
.tr-hotels__grid > .tr-hcard {
  flex: 0 1 260px;
  min-width: 260px;
  max-width: 300px;
}
@media (max-width: 640px) {
  .tr-hotels__grid > .tr-hcard { flex: 1 1 100%; max-width: 100%; }
}

/* ── Carte ── */
.tr-hcard {
  background: white;
  border-radius: 18px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  text-decoration: none;
  color: inherit;
  box-shadow: 0 4px 16px rgba(0,0,0,.06), 0 1px 4px rgba(0,0,0,.04);
  transition: transform .25s ease, box-shadow .25s ease;
  min-height: 220px;
}
.tr-hcard:hover {
  transform: translateY(-3px);
  box-shadow: 0 14px 28px rgba(0,0,0,.12), 0 2px 6px rgba(0,0,0,.04);
}

/* Vignette image (optionnelle) */
.tr-hcard__img-wrap {
  position: relative;
  aspect-ratio: 16/10;
  overflow: hidden;
  background: #eef2f6;
}
.tr-hcard__img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  transition: transform .4s ease;
}
.tr-hcard:hover .tr-hcard__img { transform: scale(1.05); }
.tr-hcard__badges-overlay {
  position: absolute;
  top: 10px;
  left: 10px;
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

/* Badges */
.tr-hcard__badge {
  display: inline-block;
  font-size: 11px;
  font-weight: 600;
  padding: 4px 10px;
  border-radius: 6px;
  background: var(--primary, #1b579e);
  color: white;
  letter-spacing: .02em;
}
.tr-hcard__badge--kosher {
  background: white;
  color: #1a1a2e;
  border: 1px solid rgba(0,0,0,.08);
}
.tr-hcard__badge--booking {
  background: #003580;
  color: #feba02;
  letter-spacing: .02em;
}
.tr-hcard__badge--airbnb {
  background: #FF385C;
  color: white;
  letter-spacing: .02em;
}

/* Overlays Booking (étoiles + prix) */
.tr-hcard__stars {
  position: absolute;
  top: 10px;
  right: 10px;
  display: inline-flex;
  align-items: center;
  gap: 2px;
  background: rgba(0,0,0,.65);
  color: #ffc53d;
  border-radius: 6px;
  padding: 4px 8px;
  font-size: 11px;
  font-weight: 600;
}
.tr-hcard__price {
  position: absolute;
  bottom: 10px;
  right: 10px;
  background: white;
  color: #1a1a2e;
  border-radius: 8px;
  padding: 5px 10px;
  font-size: 13px;
  font-weight: 800;
  box-shadow: 0 2px 6px rgba(0,0,0,.15);
}

/* Bloc note + avis */
.tr-hcard__review {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin-top: 4px;
}
.tr-hcard__score {
  background: #003580;
  color: white;
  font-weight: 800;
  font-size: 13px;
  border-radius: 6px;
  padding: 3px 8px;
  line-height: 1;
}
.tr-hcard__score--airbnb {
  background: #FF385C;
  display: inline-flex;
  align-items: center;
  gap: 3px;
}
.tr-hcard__review-count {
  font-size: 12px;
  color: #6b7280;
}

/* Body texte (sans image : padding plus grand en haut) */
.tr-hcard__body {
  padding: 16px 18px 18px;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 8px;
  flex: 1;
}
.tr-hcard__badges-top {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 6px;
  margin-bottom: 4px;
}
.tr-hcard__name {
  font-size: 17px;
  font-weight: 800;
  line-height: 1.3;
  color: #1a1a2e;
  margin: 0;
  /* Réserve la hauteur de 2 lignes pour aligner toutes les cards d'une même rangée
     même quand certains noms tiennent sur 1 ligne et d'autres sur 2. */
  min-height: calc(1.3em * 2);
  display: flex;
  align-items: flex-start;
  justify-content: center;
}
.tr-hcard__distance {
  align-self: center;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-top: auto;
  padding: 6px 12px 6px 10px;
  font-size: 13px;
  font-weight: 600;
  line-height: 1;
  color: var(--primary, #1b579e);
  background: color-mix(in srgb, var(--primary, #1b579e) 10%, white);
  border-radius: 999px;
  white-space: nowrap;
}
.tr-hcard__distance-icon {
  display: flex;
  color: var(--primary, #1b579e);
}

/* ── Empty state ── */
.tr-hotels__empty {
  text-align: center;
  padding: 64px 16px;
  color: #6b7280;
  font-size: 15px;
}
`;

function HotelCard({ context, place, category, lang }) {
    const localized = applyLang(place, lang);
    const localizedCat = category ? applyLang(category, lang) : null;
    const bethHabadLabel = getBethHabadLabel(context.travel, lang);

    const thumb = place.thumbnail || (Array.isArray(place.images) && place.images[0]) || null;
    const imgSrc = thumb
        ? (place.externalImage || /^https?:\/\//.test(thumb) ? thumb : `${ASSET_URL}${thumb}`)
        : null;

    const walkMinutes = walkMinutesFrom(context.org, place);

    const isBooking = place.source === "booking";
    const isAirbnb = place.source === "airbnb";
    const isExternal = isBooking || isAirbnb;

    let cardHref;
    if (isBooking) {
        cardHref = withAffiliate(bookingHotelUrl(place), place.affiliateId);
    } else if (isAirbnb) {
        cardHref = airbnbListingUrl(place);
    } else {
        cardHref = context.org.BASE_WEB_URL + "/travel/hotels/" + place.id;
    }

    const stars = isBooking ? Math.round(place.stars || 0) : 0;
    const rating = isExternal && place.rating ? Number(place.rating) : null;
    const reviewCount = isExternal && place.reviewCount ? place.reviewCount : null;
    const price = isExternal && place.price ? Math.round(Number(place.price)) : null;
    const currency = isExternal ? (place.currency || "EUR") : null;

    const cardInner = (
        <>
            {imgSrc && (
                    <div className="tr-hcard__img-wrap">
                        <img src={imgSrc} alt={localized.name} className="tr-hcard__img" loading="lazy" />
                        <div className="tr-hcard__badges-overlay">
                            {localizedCat?.label && (
                                <span className="tr-hcard__badge">{localizedCat.label}</span>
                            )}
                            {place.isKosher && (
                                <span className="tr-hcard__badge tr-hcard__badge--kosher">
                                    <Trans>Casher</Trans>
                                </span>
                            )}
                            {isBooking && (
                                <span className="tr-hcard__badge tr-hcard__badge--booking">
                                    Booking.com
                                </span>
                            )}
                            {isAirbnb && (
                                <span className="tr-hcard__badge tr-hcard__badge--airbnb">
                                    Airbnb
                                </span>
                            )}
                        </div>
                        {stars > 0 && (
                            <span className="tr-hcard__stars" aria-label={`${stars} étoiles`}>
                                {"★".repeat(stars)}
                            </span>
                        )}
                        {price != null && (
                            <span className="tr-hcard__price">
                                {price} {currency}
                            </span>
                        )}
                    </div>
                )}

                <div className="tr-hcard__body">
                    {!imgSrc && (
                        <div className="tr-hcard__badges-top">
                            {localizedCat?.label && (
                                <span className="tr-hcard__badge">{localizedCat.label}</span>
                            )}
                            {place.isKosher && (
                                <span className="tr-hcard__badge tr-hcard__badge--kosher">
                                    <Trans>Casher</Trans>
                                </span>
                            )}
                            {isBooking && (
                                <span className="tr-hcard__badge tr-hcard__badge--booking">
                                    Booking.com
                                </span>
                            )}
                            {isAirbnb && (
                                <span className="tr-hcard__badge tr-hcard__badge--airbnb">
                                    Airbnb
                                </span>
                            )}
                        </div>
                    )}
                    <h3 className="tr-hcard__name">{localized.name}</h3>
                    {place.note > 0 && (
                        <div style={{ marginTop: 2 }}>
                            <StarRating value={place.note} size={15} />
                        </div>
                    )}
                    {rating != null && (
                        <div className="tr-hcard__review">
                            <span className={`tr-hcard__score${isAirbnb ? " tr-hcard__score--airbnb" : ""}`}>
                                {isAirbnb ? <>★ {rating.toFixed(2)}</> : rating.toFixed(1)}
                            </span>
                            {reviewCount != null && (
                                <span className="tr-hcard__review-count">
                                    <Trans>{reviewCount} avis</Trans>
                                </span>
                            )}
                        </div>
                    )}
                    {walkMinutes != null && (
                        <div className="tr-hcard__distance" title={t`Distance à pied du ${bethHabadLabel}`}>
                            <span className="tr-hcard__distance-icon">
                                {renderIcon({ name: "IconWalk", source: "tabler" }, 16)}
                            </span>
                            <Trans>{walkMinutes} min du {bethHabadLabel}</Trans>
                        </div>
                    )}
                </div>
        </>
    );

    if (isExternal) {
        return (
            <a className="tr-hcard" href={cardHref} target="_blank" rel="noopener noreferrer">
                {cardInner}
            </a>
        );
    }

    return (
        <Link href={cardHref} legacyBehavior>
            <a className="tr-hcard">
                {cardInner}
            </a>
        </Link>
    );
}

export default function Hotels({ context, loading }) {
    const { org, config, travel } = context;
    const bethHabadLabel = getBethHabadLabel(travel, context.lang);

    const categories = (travel?.hotel?.categories || []).map(c => applyLang(c, context.lang));
    const internalPlaces = (travel?.hotelPlaces || []).slice().sort((a, b) => (a.position || 0) - (b.position || 0));

    const bookingCfg = travel?.bookingApi;
    const bookingEnabled = !!bookingCfg?.enabled;
    const affiliateId = bookingCfg?.affiliateId || null;
    const bookingCategoryId = bookingCfg?.categoryId || null;

    const airbnbCfg = travel?.airbnbApi;
    const airbnbEnabled = !!airbnbCfg?.enabled;
    const airbnbCategoryId = airbnbCfg?.categoryId || null;

    // Les hôtels Booking viennent directement de la BDD (pas d'appel API client).
    // Affichés uniquement si bookingApi.enabled. Si aucune catégorie n'est configurée,
    // les hôtels tombent dans la section "Autres" (categoryId null).
    // La catégorie et l'affiliateId sont appliqués au rendu → modifiables sans re-import.
    const bookingPlaces = React.useMemo(() => {
        if (!bookingEnabled) return [];
        return (travel?.bookingPlaces || []).map((b) => ({
            ...b,
            categoryId: bookingCategoryId,
            source: "booking",
            affiliateId,
            externalImage: true,
        }));
    }, [bookingEnabled, travel?.bookingPlaces, affiliateId, bookingCategoryId]);

    const airbnbPlaces = React.useMemo(() => {
        if (!airbnbEnabled) return [];
        return (travel?.airbnbPlaces || []).map((a) => ({
            ...a,
            categoryId: airbnbCategoryId,
            source: "airbnb",
            externalImage: true,
        }));
    }, [airbnbEnabled, travel?.airbnbPlaces, airbnbCategoryId]);

    const allPlaces = React.useMemo(
        () => [...internalPlaces, ...bookingPlaces, ...airbnbPlaces],
        [internalPlaces, bookingPlaces, airbnbPlaces]
    );

    const usedCatIds = new Set(allPlaces.map(p => p.categoryId).filter(Boolean));
    const activeCategories = categories.filter(c => usedCatIds.has(c.id));

    const [activeCat, setActiveCat] = React.useState(null);

    const visiblePlaces = activeCat
        ? allPlaces.filter(p => p.categoryId === activeCat)
        : allPlaces;

    const placesByCategory = React.useMemo(() => {
        const map = new Map();
        for (const cat of activeCategories) {
            map.set(cat.id, visiblePlaces.filter(p => p.categoryId === cat.id));
        }
        const orphans = visiblePlaces.filter(p => !p.categoryId || !activeCategories.find(c => c.id === p.categoryId));
        if (orphans.length > 0) map.set("__other__", orphans);
        return map;
    }, [visiblePlaces, activeCategories]);

    const categoryById = React.useMemo(() => {
        const map = new Map();
        for (const c of categories) map.set(c.id, c);
        return map;
    }, [categories]);

    // JSON-LD ItemList d'hébergements — améliore l'affichage dans Google.
    const itemListElements = allPlaces.slice(0, 50).map((p, i) => {
        const loc = applyLang(p, context.lang);
        const pImg = p.thumbnail || (Array.isArray(p.images) && p.images[0]) || null;
        const imgUrl = pImg ? (p.externalImage || /^https?:\/\//.test(pImg) ? pImg : `${ASSET_URL}${pImg}`) : undefined;
        return {
            "@type": "ListItem",
            position: i + 1,
            item: {
                "@type": "LodgingBusiness",
                name: loc.name,
                image: imgUrl,
                address: p.address?.address ? {
                    "@type": "PostalAddress",
                    streetAddress: p.address.address,
                } : undefined,
                geo: (p.address?.lat && p.address?.lon) ? {
                    "@type": "GeoCoordinates",
                    latitude: p.address.lat,
                    longitude: p.address.lon,
                } : undefined,
                starRating: p.stars ? { "@type": "Rating", ratingValue: p.stars } : undefined,
                aggregateRating: p.rating ? {
                    "@type": "AggregateRating",
                    ratingValue: p.rating,
                    reviewCount: p.reviewCount || 1,
                } : undefined,
            },
        };
    });
    const jsonLd = itemListElements.length > 0 ? {
        "@context": "https://schema.org",
        "@type": "ItemList",
        name: t`Hébergements proches du ${bethHabadLabel} de ${org.ville || org.name}`,
        itemListElement: itemListElements,
    } : null;

    return (
        <div className="travel-page travel-page--hotels tr-hotels">
            <style dangerouslySetInnerHTML={{ __html: hotelStyles }} />
            <Meta
                context={context}
                title={t`Hébergements proches du ${bethHabadLabel} de ${org.ville || org.name}`}
                description={config["meta.description"] || t`Hôtels, appartements et hébergements proches du ${bethHabadLabel} de ${org.ville || org.name}`}
                jsonLd={jsonLd}
            />

            {loading && <Loader />}
            <Header context={context} />

            <main className="travel-main">
                <div className="travel-container">

                    <h1 className="tr-hotels__title">
                        <Trans>Hébergements proches du {bethHabadLabel}</Trans>{" "}{org.ville || org.name}
                    </h1>

                    {activeCategories.length > 0 && (
                        <div className="tr-hotels__cats">
                            {activeCategories.map(cat => (
                                <button
                                    key={cat.id}
                                    className={`tr-hotels__cat${activeCat === cat.id ? " tr-hotels__cat--active" : ""}`}
                                    onClick={() => setActiveCat(activeCat === cat.id ? null : cat.id)}
                                >
                                    {cat.label}
                                </button>
                            ))}
                        </div>
                    )}

                    {allPlaces.length === 0 && (
                        <div className="tr-hotels__empty">
                            <Trans>Aucun hébergement à afficher pour le moment.</Trans>
                        </div>
                    )}

                    {Array.from(placesByCategory.entries()).map(([catId, places]) => {
                        if (places.length === 0) return null;
                        const cat = catId === "__other__" ? null : categoryById.get(catId);
                        const catLocalized = cat ? applyLang(cat, context.lang) : null;
                        return (
                            <section key={catId} className="tr-hotels__section">
                                <h2 className="tr-hotels__section-title">
                                    {catLocalized?.label || <Trans>Autres</Trans>}
                                </h2>
                                <div className="tr-hotels__grid">
                                    {places.map(place => (
                                        <HotelCard
                                            context={context}
                                            key={place.id}
                                            place={place}
                                            category={cat}
                                            lang={context.lang}
                                        />
                                    ))}
                                </div>
                            </section>
                        );
                    })}

                </div>
            </main>

            <Footer context={context} />
        </div>
    );
}
